import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { jsPDF } from 'jspdf';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import 'svg2pdf.js';
import { PDFDocument, PDFName, PDFArray } from 'pdf-lib';
import { MatDialog } from '@angular/material/dialog';
import { PdfPreviewModalComponent } from '@shared/components';

@Injectable({
  providedIn: 'root'
})
export class PdfGeneratorCorteService {
  _http = inject(HttpClient);
  _dialog = inject(MatDialog);
  _apiUrl = environment.apiUrl;
  _authHeaders = new HttpHeaders({
    Authorization: '',
  });

  private curves: BehaviorSubject<Array<{ svg: string; text: string[]; color: string }>> = new BehaviorSubject<Array<{ svg: string; text: string[]; color: string }>>([]);

  constructor() { }

  clearCurves(): void {
    this.curves.next([]);
  }

  addCurve(svgString: string, text: string[] = [], color: string = '#000000'): void {
    const currentCurves = this.curves.value;
    this.curves.next([...currentCurves, { svg: svgString, text, color }]);
  }

  // Métodos deprecados, ahora se pasan los textos directamente a addCurve
  addTextSF(text: string[], curveTextColor: string): void { }
  addTextFL(text: string[], curveTextColor: string): void { }

  getCurves(): Observable<{ svg: string; text: string[]; color: string; }[]> {
    return this.curves.asObservable();
  }

  async exportSvgToPdf(fileName: string, pixelspacing: any, backgroundPdfUrl: string): Promise<File> {
    const response = await fetch(backgroundPdfUrl);
    const bgBytes = await response.arrayBuffer();
    const bgDoc = await PDFDocument.load(bgBytes);
    const [firstPage] = bgDoc.getPages();
    const { width: bgW_pts, height: bgH_pts } = firstPage.getSize();

    // Convert pts to mm (1pt = 0.352778mm)
    const pageW = bgW_pts * 0.352778;
    const pageH = bgH_pts * 0.352778;

    // Margen extra a la derecha (mm). El bg se ancla a la izquierda en el
    // merge, así que esta extensión cae como espacio blanco a la derecha.
    const extraRightMm = 60;
    const outPageW = pageW + extraRightMm;

    // Espacio extra abajo (mm). El bg se ancla al TOP de la página en el
    // merge (ver `mergeBackgroundWithOverlay`), por lo que esta extensión
    // cae como espacio en blanco al pie del documento — sin desplazar el
    // contenido del template ni dejar aire arriba.
    const EXTRA_BOTTOM_MM = 100;
    const outPageH = pageH + EXTRA_BOTTOM_MM;

    // 1. Generar overlay con curvas (Páginas 1..N con background)
    const curves = this.curves.value;
    const overlayBytes = await this.createOverlayPdf(curves, pixelspacing, outPageW, outPageH);

    // 2. Mergear todo con pdf-lib
    const finalBlob = await this.mergeBackgroundWithOverlay(bgBytes, overlayBytes, fileName);

    // Abrir modal de previsualización
    this._dialog.open(PdfPreviewModalComponent, {
      data: {
        pdfBlob: finalBlob,
        fileName: fileName
      },
      maxWidth: '90vw',
      width: '1000px'
    });

    const file = new File([finalBlob], `${fileName}.pdf`, { type: 'application/pdf' });
    return file;
  }


  private parseSvgSize(svgString: string) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgString, 'image/svg+xml');
    const svgElement = doc.documentElement;

    let width = parseFloat(svgElement.getAttribute('width') || '0');
    let height = parseFloat(svgElement.getAttribute('height') || '0');
    let viewBox = svgElement.getAttribute('viewBox');

    if (viewBox && (width === 0 || height === 0)) {
      const parts = viewBox.split(' ');
      if (parts.length === 4) {
        width = parseFloat(parts[2]);
        height = parseFloat(parts[3]);
      }
    }

    if (!viewBox && width && height) {
      viewBox = `0 0 ${width} ${height}`;
    }

    return { width, height, viewBox, svgElement };
  }

  private async createOverlayPdf(curves: any[], pixelspacing: any, pageW: number, pageH: number): Promise<Uint8Array> {
    const pdf = new jsPDF({
      orientation: pageW > pageH ? 'l' : 'p',
      unit: 'mm',
      format: [pageW, pageH]
    });

    const marginX = 10;

    // Layout del stack de curvas (todas las páginas):
    //  - `TOP_OFFSET_MM`: aire vertical SOLO sobre el primer implante, para
    //    que no pise el header del template (`000000 Corte barras`, datos del
    //    paciente, leyenda de colores, "esc 1:1").
    //  - `GAP_MM`: separación vertical entre el FONDO DEL CUERPO de un
    //    implante y el TOP del cuerpo del siguiente. NO entre bounding boxes
    //    completos — las patas/tabs de cada implante overlappean libremente
    //    con el espacio lateral vacío de la siguiente curva (las tabs están
    //    sobre los costados; el cuerpo del siguiente va al centro → no
    //    colisionan visualmente).
    //  - `IMPLANT_THICKNESS_MM`: espesor del implante (igual al
    //    `fixedOffsetMm` del drawer = 10mm). Lo sumamos al body bottom para
    //    incluir la curva desplazada como parte del cuerpo, no de las patas.
    //  - `HORIZONTAL_SHIFT_MM`: corrimiento horizontal respecto del centro.
    //    Positivo = más a la derecha, negativo = más a la izquierda.
    const TOP_OFFSET_MM = 60;
    // GAP negativo a propósito: el SVG del siguiente implante trae ~5mm de
    // padding superior antes de su arch peak, así que con `GAP = -10` el arch
    // peak del implante N+1 queda ~5mm POR ENCIMA del body bottom del
    // implante N — los cuerpos casi touching, con leve overlap visual que
    // matchea el layout de referencia del proveedor.
    const GAP_MM = -25;
    const IMPLANT_THICKNESS_MM = 10;
    const HORIZONTAL_SHIFT_MM = 0;

    // `currentY` se va incrementando dinámicamente según el fondo del cuerpo
    // de cada implante (usando `data-inner-end-y` del SVG), no por bounding
    // box completo. Esto permite que las patas de un implante sigan dentro
    // del slot del siguiente sin pisar su cuerpo.
    let currentY = TOP_OFFSET_MM;

    for (let i = 0; i < curves.length; i++) {
      const curveData = curves[i];

      // Dibujar bloques de información
      if (i === 0) {
        // Título en la primera página
        pdf.setFontSize(22);
        pdf.setTextColor(0, 0, 0);
        pdf.text('', pageW / 2, 20, { align: 'center' });
      }


      const { width: svgWidth, height: svgHeight, viewBox, svgElement } = this.parseSvgSize(curveData.svg);
      if (svgWidth === 0 || svgHeight === 0) continue;

      const vbWidthMm = pixelspacing.width * svgWidth;
      const vbHeightMm = pixelspacing.height * svgHeight;

      const svgNS = 'http://www.w3.org/2000/svg';

      const g = svgElement.ownerDocument.createElementNS(svgNS, 'g');

      while (svgElement.firstChild) {
        g.appendChild(svgElement.firstChild);
      }

      svgElement.appendChild(g);

      svgElement.setAttribute('width', svgWidth.toString());
      svgElement.setAttribute('height', svgHeight.toString());
      svgElement.setAttribute('viewBox', `0 0 ${svgWidth} ${svgHeight}`);
      svgElement.setAttribute('preserveAspectRatio', 'xMinYMid meet');
      const rotW = vbWidthMm;
      const rotH = vbHeightMm;
      const availableWidth = pageW - marginX * 2;
      const scale = 1; //Math.min(1, availableWidth / rotW);

      // Centrado horizontal (con shift opcional para tuning fino).
      const x = (pageW - rotW * scale) / 2 + HORIZONTAL_SHIFT_MM;
      // Posición vertical: arrancamos desde `currentY` (que el iteración
      // anterior dejó posicionado justo después del cuerpo del implante
      // anterior + GAP). En la primera iteración = TOP_OFFSET.
      const y = currentY;

      // pdf.svg works with current jsPDF unit (mm)
      //Posicion y medidas del implante.
      await pdf.svg(svgElement, {
        x: x,
        y: y,
        width: rotW ,
        height: rotH
      });

      const lineSpacing = 5;
      const blockHeight = lineSpacing * Math.max(0, curveData.text.length - 1);
      const blockWidth = lineSpacing * Math.max(0, curveData.text.length - 1);
      const flapPadding = 2;
      const flapWidth = Math.max(20, blockWidth + flapPadding * 2);

      // Dibujar línea de medida (a la izquierda de la curva)
      const flapLeft = Math.max(0, x - flapWidth);
      const lineX = x;
      const innerStartXAttr = svgElement.getAttribute('data-inner-start-x');
      const innerEndXAttr = svgElement.getAttribute('data-inner-end-x');
      let lineY1: number;
      let lineY2: number;

      if (innerStartXAttr !== null && innerEndXAttr !== null) {
        const innerStartX = parseFloat(innerStartXAttr);
        const innerEndX = parseFloat(innerEndXAttr);
        const innerStartMm = innerStartX * pixelspacing.width;
        const innerEndMm = innerEndX * pixelspacing.width;
        const minMm = Math.min(innerStartMm, innerEndMm);
        const maxMm = Math.max(innerStartMm, innerEndMm);
        lineY1 = y + minMm * scale;
        lineY2 = y + maxMm * scale;
      } else {
        const extraFlap = 10; // mm adicionales arriba y abajo
        lineY1 = y - extraFlap;
        lineY2 = y + rotH * scale + extraFlap;
      }

      // Asegurar que la línea quede dentro de la página
      lineY1 = Math.max(0, lineY1);
      lineY2 = Math.min(pageH, lineY2);

      const flapRight = x;
      const curveLeft = x;
      const curveRight = x + (rotW * scale);
      const horizontalPadding = 2;
      const desiredInset = Math.min(12, (rotW * scale) * 0.15);
      const desiredTextX = curveLeft + desiredInset + blockWidth + 60;
      const minTextX = curveLeft + horizontalPadding + blockWidth;
      const maxTextX = curveRight - horizontalPadding;
      const textX = Math.max(minTextX, Math.min(desiredTextX, Math.max(minTextX, maxTextX)));
      const verticalPadding = 2;
      const desiredTextY = ((lineY1 + lineY2) / 2) - 3 - 40;
      const minTextY = lineY1 + verticalPadding;
      const maxTextY = lineY2 - verticalPadding;
      const textY = Math.max(minTextY, Math.min(desiredTextY, Math.max(minTextY, maxTextY)));
      const innerStartX = innerStartXAttr ?parseFloat(innerStartXAttr):0;
      const innerEndX = innerEndXAttr ? parseFloat(innerEndXAttr) : 0;
      const innerStartMm = innerStartX * pixelspacing.width;
      const innerEndMm = innerEndX * pixelspacing.width;
      const minMm = Math.min(innerStartMm, innerEndMm);
      const maxMm = Math.max(innerStartMm, innerEndMm);
      //this.drawTextBlock(pdf, curveData.text, curveData.color, innerStartMm + 40, minTextY, { angle: 0 });

      pdf.saveGraphicsState();
      pdf.setDrawColor(0, 0, 0);
      pdf.setLineWidth(0.2);
           pdf.restoreGraphicsState();

      // Avanzar `currentY` para la siguiente curva.
      // El "cuerpo" del implante termina en la curva desplazada (la inferior
      // del par paralelo). Eso está a innerEndY (la baseline de la curva
      // base) + IMPLANT_THICKNESS_MM (la separación entre la base y la
      // desplazada). Más allá quedan las tabs/patas que pueden flotar en
      // el espacio lateral vacío de la siguiente curva sin pisar su cuerpo.
      const innerEndYAttr = svgElement.getAttribute('data-inner-end-y');
      const innerStartYAttr = svgElement.getAttribute('data-inner-start-y');
      const innerEndYPx = innerEndYAttr !== null ? parseFloat(innerEndYAttr) : 0;
      const innerStartYPx = innerStartYAttr !== null ? parseFloat(innerStartYAttr) : 0;
      const baselineYPx = Math.max(innerEndYPx, innerStartYPx);
      const bodyBottomFromSvgTopMm = baselineYPx > 0
        ? baselineYPx * pixelspacing.height + IMPLANT_THICKNESS_MM
        : rotH * scale; // fallback: stack por bounding box si el SVG no trae los attrs
      currentY = y + bodyBottomFromSvgTopMm + GAP_MM;
    }

    return new Uint8Array(pdf.output('arraybuffer'));
  }


private async mergeBackgroundWithOverlay(
    bgBytes: ArrayBuffer,
    overlayBytes: Uint8Array,
    fileName: string
): Promise<Blob> {
  const bgDoc = await PDFDocument.load(bgBytes);
  const ovDoc = await PDFDocument.load(overlayBytes);

  const outDoc = await PDFDocument.create();
  outDoc.setTitle(fileName);
  outDoc.setAuthor('ScanMed');
  outDoc.setSubject(fileName);
  outDoc.setProducer('ScanMed');
  outDoc.setCreator('ScanMed');
  outDoc.setCreationDate(new Date());
  outDoc.setModificationDate(new Date());

  const bgPage0 = bgDoc.getPage(0);
  const { width: bgW, height: bgH } = bgPage0.getSize();

  // Embed background UNA sola vez (reutilizable)
  const bgEmbedded = await outDoc.embedPage(bgPage0);

  for (let i = 0; i < ovDoc.getPageCount(); i++) {
  // 1) Copiar la página overlay tal cual (paths quedan “reales” en la página)
  const [page] = await outDoc.copyPages(ovDoc, [i]);
  outDoc.addPage(page);

  // Guardar el/los content streams originales (overlay)
  const originalContents = page.node.get(PDFName.of('Contents'));

  // 2) Vaciar contents momentáneamente para que drawPage genere SOLO el stream de background
  //    (si Contents es ref o array, lo reemplazamos por array vacío)
  page.node.set(PDFName.of('Contents'), outDoc.context.obj([]));

  // 3) Dibujar background anclado al TOP de la página. En PDF y=0 está
  //    abajo, así que para que el template del proveedor quede pegado
  //    arriba (y el espacio extra que agregamos en `exportSvgToPdf` caiga
  //    como aire al pie), su esquina inferior tiene que estar en
  //    `pageHeight - bgH`. Sin esto, el bg se quedaba a 150pt del fondo y
  //    el extra crecía como espacio blanco arriba del header.
  const { height: outPageHeightPts } = page.getSize();
  page.drawPage(bgEmbedded, {
    x: 0,
    y: outPageHeightPts - bgH,
    width: bgW,
    height: bgH,
  });

  // Capturar contents generado por drawPage (background)
  const bgContentsNow = page.node.get(PDFName.of('Contents'));

  // 4) Re-armar Contents como: [background, overlayOriginal]
  const finalContents = outDoc.context.obj([]);

  const pushContents = (c: any) => {
    if (!c) return;
    if (c instanceof PDFArray) {
      for (let idx = 0; idx < c.size(); idx++) {
        finalContents.push(c.get(idx));
      }
    } else {
      finalContents.push(c);
    }
  };

  pushContents(bgContentsNow);
  pushContents(originalContents);

  page.node.set(PDFName.of('Contents'), finalContents);
}

const pdfBytes = await outDoc.save();
return new Blob([pdfBytes], { type: 'application/pdf' });
}
  private drawTextBlock(
      pdf: jsPDF,
      lines: string[],
      color: string,
      x: number,
      y: number,
      options?: { angle?: number }
  ): void {
    const lineSpacing = 5;

    pdf.setDrawColor(color);
    pdf.setLineWidth(0.4);

    pdf.setTextColor(color);

    let yPos = y;
    lines.forEach((line: string, index: number) => {
      const fontSize = index === 0 ? 14 : 12;
      pdf.setFontSize(fontSize);
      pdf.text(line, x, yPos, { angle: options?.angle  ?? 0 });
      yPos += lineSpacing;
    });
  }



  sendPdfErkom(
      orderId: number,
      procedureId: number,
      implantTypeId: number,
      quantity: number,
      file: any
  ): Observable<any> {

    const body = {
      procedure_id: procedureId,
      implant_type_id: implantTypeId,
      quantity: quantity,
      file: file,
    };
    return this._http.post(
        `${this._apiUrl}/api/order/${orderId}/upload-implant-document`,
        body,
        {
          headers: this._authHeaders,
        }
    );
  }
}
